import { toast } from "@/components/ui/sonner";
import {
  getRegistrantAccountUserOptions,
  getRegistrantAccountUsersOptions,
  updateRegistrantAccountUserMutation,
} from "@/hera-api/client/@tanstack/react-query.gen";
import type { UpdateRegistrantAccountRequestSchema } from "@/hera-api/client/types.gen";
import { useAuthQuerySetup } from "@/hooks/use-auth-query-setup";
import { getQueryErrorMessage } from "@/lib/utils";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

export function useRegistrantUserForm(userId: string | null) {
  const queryClient = useQueryClient();
  const { isAuthSetupComplete } = useAuthQuerySetup();

  // Get user data
  const {
    data: userData,
    isLoading: isLoadingUser,
    error: errorUser,
  } = useQuery({
    ...getRegistrantAccountUserOptions({
      path: {
        user_id: userId as string,
      },
    }),
    enabled: isAuthSetupComplete && !!userId,
  });

  const updateUserMutationFn = useMutation({
    ...updateRegistrantAccountUserMutation(),
  });

  const handleSubmit = async (data: UpdateRegistrantAccountRequestSchema) => {
    if (!userId) {
      throw new Error("User ID is required for updates");
    }

    try {
      // Updating an existing user
      await updateUserMutationFn.mutateAsync({
        path: { user_id: userId },
        body: data,
      });
      toast({
        title: "Success",
        description: "User updated successfully",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: `${getQueryErrorMessage(error)}`,
        variant: "destructive",
      });
      throw error;
    }

    // Invalidate queries to refresh data
    queryClient.invalidateQueries({
      queryKey: getRegistrantAccountUsersOptions().queryKey,
    });
    queryClient.invalidateQueries({
      queryKey: getRegistrantAccountUserOptions({
        path: { user_id: userId },
      }).queryKey,
    });
  };

  return {
    user: userData ?? null,
    // Data
    userData: userData
      ? {
          primary_id: userData.primary_id,
          email: userData.email,
          name: userData.name ?? "",
          phone: userData.phone ?? "",
          company_name: null, // Secondary accounts don't have company_name
          address1: null,
          address2: null,
          city: null,
          state: null,
          zipcode: null,
        }
      : null,

    // Loading states
    isLoading: isLoadingUser,

    // Errors
    error: errorUser,

    // Actions
    handleSubmit,
  };
}
